export const PLAN_RANK = {
  Starter: 1,
  Basic: 2,
  Pro: 3,
  Advanced: 4,
  Enterprise: 5,
};

export const planRank = planName => PLAN_RANK[planName] ?? 0;

export const normalizeInterval = interval => interval || 'monthly';

export const isSamePlanAndInterval = (
  currentPlanName,
  targetPlanName,
  currentInterval,
  targetInterval
) =>
  currentPlanName === targetPlanName &&
  normalizeInterval(currentInterval) === normalizeInterval(targetInterval);

export const inferIntervalChangeType = (currentInterval, targetInterval) => {
  const current = normalizeInterval(currentInterval);
  const target = normalizeInterval(targetInterval);

  if (current === target) return null;
  if (current === 'monthly' && target === 'yearly') return 'upgrade';
  if (current === 'yearly' && target === 'monthly') return 'downgrade';

  return null;
};

export const inferChangeType = (
  currentPlanName,
  targetPlanName,
  { currentInterval, targetInterval } = {}
) => {
  const currentRank = planRank(currentPlanName);
  const targetRank = planRank(targetPlanName);

  if (targetRank > currentRank) return 'upgrade';
  if (targetRank < currentRank) return 'downgrade';

  return (
    inferIntervalChangeType(currentInterval, targetInterval) || 'change'
  );
};

export const hasActiveSubscription = (account, billing = {}) => {
  if (billing?.subscribed) return true;

  return Boolean(
    billing?.plan_name &&
      !billing?.on_trial &&
      !billing?.trial_expired
  );
};

export const shouldUsePlanChange = ({
  account,
  billing = {},
  currentPlanName,
  targetPlanName,
  currentInterval,
  targetInterval,
}) => {
  if (!currentPlanName || !targetPlanName) return false;

  if (
    isSamePlanAndInterval(
      currentPlanName,
      targetPlanName,
      currentInterval,
      targetInterval
    )
  ) {
    return false;
  }

  if (planRank(targetPlanName) === planRank(currentPlanName) && currentPlanName !== targetPlanName) {
    return false;
  }

  // Trial-only accounts should still go through checkout.
  if (billing?.on_trial && !billing?.subscribed) return false;

  return hasActiveSubscription(account, billing);
};

export const normalizePlanChangePreview = (
  data,
  { targetPlanName, currentPlanName, currentInterval, targetInterval } = {}
) => {
  if (!data) return null;

  const targetPlan = data.targetPlan || data.target_plan || targetPlanName;
  const currentPlan = data.currentPlan || data.current_plan || currentPlanName;
  const changeType =
    data.changeType ||
    data.change_type ||
    inferChangeType(currentPlan, targetPlan, {
      currentInterval,
      targetInterval: data.interval || targetInterval,
    });

  return {
    changeType,
    currentPlan,
    targetPlan,
    interval: data.interval,
    effectiveAt: data.effectiveAt || data.effective_at,
    currency: data.currency || 'eur',
    amountDueCents: data.amountDueCents ?? data.amount_due_cents ?? 0,
    agents: data.agents,
    pendingPlan: data.pendingPlan || data.pending_plan,
    lineItems: data.lineItems || data.line_items || [],
    prorationSummary: normalizeProrationSummary(
      data.prorationSummary || data.proration_summary,
      { currentPlan, targetPlan, amountDueCents: data.amountDueCents ?? data.amount_due_cents }
    ),
  };
};

const normalizeProrationSummary = (summary, { currentPlan, targetPlan, amountDueCents } = {}) => {
  if (!summary && !amountDueCents) return null;

  const raw = summary || {};

  return {
    currentPlan: raw.currentPlan || raw.current_plan || currentPlan,
    targetPlan: raw.targetPlan || raw.target_plan || targetPlan,
    newPlanChargeCents: raw.newPlanChargeCents ?? raw.new_plan_charge_cents ?? 0,
    currentPlanCreditCents:
      raw.currentPlanCreditCents ?? raw.current_plan_credit_cents ?? 0,
    amountDueCents:
      raw.amountDueCents ?? raw.amount_due_cents ?? amountDueCents ?? 0,
    daysLeft: raw.daysLeft ?? raw.days_left,
    periodDays: raw.periodDays ?? raw.period_days,
  };
};
